import { v4 as uuid } from 'uuid';
import React, {
  useState,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
} from 'react';
import without from 'lodash/without';
import isEmpty from 'lodash/isEmpty';
import { useIntl } from 'react-intl';
import {
  withBlockExtensions,
  getBlocksLayoutFieldname,
} from '@plone/volto/helpers';

import {
  SidebarPortal,
  BlockDataForm,
  BlocksToolbar,
} from '@plone/volto/components';
import PropTypes from 'prop-types';
import View, { BlockDataProps, LeriItem } from './View';
import Schema from './schema';
// eslint-disable-next-line import/no-unresolved
import { Content } from '@plone/types';

interface EditProps {
  block: string;
  data: BlockDataProps;
  onChangeBlock: (blockId: string, newData: BlockDataProps) => void;
  selected: boolean;
  blocksConfig: any;
  content: Content;
  pathname: string;
  manage: boolean;
  setSidebarTab: (tabIndex: number) => void;
  onFocusNextBlock: (blockId: string, element: HTMLElement) => void;
}

interface BlockDataUpdate {
  blocks: Record<string, any>;
  blocks_layout: {
    items: string[];
  };
  [key: string]: any;
}

const Edit: React.FC<EditProps> = (props) => {
  const { block, data, onChangeBlock, selected, blocksConfig } = props;
  const intl = useIntl();

  const [multiSelected, setMultiSelected] = useState<string[]>([]);
  const blockItemMap: Record<string, number> = {};

  let changed = !data?.items;
  data.items = (
    data?.items || [
      {
        '@id': uuid(),
      } as LeriItem,
    ]
  ).map((item, i) => {
    if (isEmpty(item?.blocks)) {
      const id = uuid();
      item.blocks = {
        [id]: {
          '@type': 'slate',
          plaintext: '',
          value: [{ children: [{ text: '' }], type: 'p' }],
        },
      };
      item.blocks_layout = {
        items: [id],
      };
      changed = true;
    }
    item?.blocks_layout?.items.forEach((blockId: string) => {
      blockItemMap[blockId] = i;
    });
    return item;
  });

  if (changed) {
    onChangeBlock(block, { ...data });
  }

  const [selectedItem, setSelectedItem] = useState<number>(0);
  const [selectedBlock, setSelectedBlock] = useState<string | null>(
    data.items?.[selectedItem]?.blocks_layout?.items?.[0] ?? null,
  );

  const schema = Schema(intl);

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const hasBlockActive = !!selectedBlock && selectedItem !== null;

    if (!hasBlockActive) {
      return;
    }

    const item = data?.items?.[selectedItem];
    const selectedIndex = item?.blocks_layout?.items.indexOf(selectedBlock);

    if (e.key === 'Enter') {
      const newData: BlockDataProps = { ...data };
      const id = uuid();
      if (
        newData &&
        newData.items &&
        newData.items[selectedItem] &&
        newData.items[selectedItem].blocks
      ) {
        newData.items[selectedItem].blocks[id] = {
          '@type': 'slate',
          plaintext: '',
          value: [{ children: [{ text: '' }], type: 'p' }],
        };
      }
      if (
        newData &&
        newData.items &&
        newData.items[selectedItem] &&
        newData.items[selectedItem].blocks_layout
      ) {
        newData.items[selectedItem].blocks_layout.items = [
          ...item?.blocks_layout?.items.slice(0, selectedIndex + 1),
          id,
          ...item?.blocks_layout?.items.slice(selectedIndex + 1),
        ];
      }
      onChangeBlock(block, newData);
      props.onFocusNextBlock(selectedBlock, e.target as HTMLElement);
      setSelectedBlock(id);
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const onSelectBlock = (
    id: string,
    isMultipleSelection: boolean,
    event:
      | ReactMouseEvent<HTMLElement>
      | ReactKeyboardEvent<HTMLElement>
      | undefined,
    activeBlock: string | null,
  ) => {
    let newMultiSelected: string[] = [];
    let selectedBlockId: string | null = id;
    const itemIndex = blockItemMap[id];
    const item = data.items?.[itemIndex];

    if (isMultipleSelection && event) {
      selectedBlockId = null;

      const blocksLayoutFieldname = getBlocksLayoutFieldname(item as LeriItem);
      const layoutValue = item?.[blocksLayoutFieldname as keyof LeriItem] as
        | { items: string[] }
        | undefined;

      const blocks_layout = layoutValue?.items ?? [];

      if (event.shiftKey) {
        const anchor =
          multiSelected.length > 0
            ? blocks_layout.indexOf(multiSelected[0])
            : blocks_layout.indexOf(activeBlock as string);
        const focus = blocks_layout.indexOf(id);
        if (anchor === focus) {
          newMultiSelected = [id];
        } else if (focus > anchor) {
          newMultiSelected = [...blocks_layout.slice(anchor, focus + 1)];
        } else {
          newMultiSelected = [...blocks_layout.slice(focus, anchor + 1)];
        }
      }

      if ((event.ctrlKey || event.metaKey) && !event.shiftKey) {
        if (multiSelected.includes(id)) {
          selectedBlockId = null;
          newMultiSelected = without(multiSelected, id) as string[];
        } else {
          newMultiSelected = [...(multiSelected || []), id];
        }
      }
    }

    setSelectedItem(itemIndex);
    setSelectedBlock(selectedBlockId);
    setMultiSelected(newMultiSelected);
  };

  const changeBlockData = (newBlockData: BlockDataUpdate) => {
    const item = data?.items?.[selectedItem];
    const pastedBlocks = newBlockData.blocks_layout.items.filter((blockID) => {
      return !item?.blocks_layout?.items?.find((x: string) => x === blockID);
    });

    const selectedIndex =
      (item?.blocks_layout?.items?.indexOf(selectedBlock as string) ?? -1) + 1;

    const newData: BlockDataProps = {
      ...data,
    };

    if (newData.items) {
      newData.items[selectedItem] = {
        ...newData.items[selectedItem],
        ...newBlockData,
        blocks_layout: {
          items: [
            ...item?.blocks_layout?.items.slice(0, selectedIndex),
            ...pastedBlocks,
            ...item?.blocks_layout?.items.slice(selectedIndex),
          ],
        },
      };
    }

    onChangeBlock(block, newData);
  };

  return (
    /* eslint-disable jsx-a11y/no-static-element-interactions */
    <div
      onKeyDown={handleKeyDown}
      // The tabIndex is required for the keyboard navigation
      /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
      tabIndex={-1}
    >
      <button
        className="block__edit"
        onClick={() => {
          setSelectedBlock(null);
          setSelectedItem(0);
          props.setSidebarTab(1);
        }}
      >
        Abschnitt und Einträge bearbeiten
      </button>
      <View
        {...props}
        isEdit
        selectedItem={selectedItem}
        selectedBlock={selectedBlock}
        onSelectBlock={onSelectBlock}
        multiSelected={multiSelected}
      />
      {selected ? (
        <BlocksToolbar
          selectedBlock={selectedBlock || undefined}
          selectedBlocks={multiSelected}
          onSetSelectedBlocks={(blockIds: string[]) => {
            setMultiSelected(blockIds);
          }}
          formData={data.items[selectedItem]}
          onSelectBlock={(
            id: string,
            isMultiple: boolean,
            e:
              | React.MouseEvent<HTMLElement>
              | React.KeyboardEvent<HTMLElement>
              | undefined,
          ) => {
            const isMultipleSelection = e
              ? e.shiftKey || e.ctrlKey || e.metaKey
              : false;

            onSelectBlock(id, isMultipleSelection, e, selectedBlock);
          }}
          onChangeBlocks={(newBlockData: BlockDataUpdate) => {
            changeBlockData(newBlockData);
          }}
        />
      ) : (
        ''
      )}
      <SidebarPortal selected={selected && !selectedBlock}>
        <BlockDataForm
          schema={schema}
          title={schema?.title}
          onChangeField={(id: string, value: any) => {
            onChangeBlock(block, {
              ...data,
              [id]: value,
            });
          }}
          onChangeBlock={onChangeBlock}
          formData={data}
          block={block}
          blocksConfig={blocksConfig}
        />
      </SidebarPortal>
    </div>
  );
};

Edit.propTypes = {
  block: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  onChangeBlock: PropTypes.func.isRequired,
  pathname: PropTypes.string.isRequired,
  selected: PropTypes.bool.isRequired,
  manage: PropTypes.bool.isRequired,
};

export default withBlockExtensions(Edit);
